import { getRetailer } from "@faire/marketplace-lib/serialized-data/getRetailer";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { PaymentTerm } from "@faire/web-api--source/indigofair/data/PaymentTerm";
import React, { createContext, useContext, useMemo } from "react";
import { createStore, StoreApi, useStore } from "zustand";

import { useRetailerCoreWithSelector } from "./RetailerCoreProvider";

interface RetailerPaymentTerm {
  paymentOnShipmentTerm: keyof typeof PaymentTerm;
  paymentTermData: IRetailer["payment_term_data"];
  maximumAvailablePaymentTerm: keyof typeof PaymentTerm;
  maximumAvailablePaymentTermData: IRetailer["maximum_available_payment_term_data"];
  paymentTerm: keyof typeof PaymentTerm;
  isRetailerOnNetTerms: boolean;
  isRetailerQualifiedForTerms: boolean;
  isRetailerPreQualifiedForLimitIncrease: boolean;
}

const RetailerPaymentTermContext = createContext<
  StoreApi<RetailerPaymentTerm> | undefined
>(undefined);

export const RetailerPaymentTermProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const initialRetailer = getRetailer();
  const retailer = useRetailerCoreWithSelector((state) => state.retailer);
  const store = useMemo(
    () =>
      createStore<RetailerPaymentTerm>(() => {
        const paymentOnShipmentTerm =
          retailer?.payment_on_shipment_term_data?.payment_term ??
          initialRetailer?.payment_on_shipment_term_data?.payment_term ??
          PaymentTerm.PAYMENT_ON_SHIPMENT;

        const paymentTerm =
          (retailer?.payment_term ?? initialRetailer?.payment_term) ===
          PaymentTerm.HOLD_ON_PLACEMENT
            ? PaymentTerm.PAYMENT_ON_SHIPMENT
            : (retailer?.payment_term ??
              initialRetailer?.payment_term ??
              PaymentTerm.UNSPECIFIED);

        const isRetailerQualifiedForTerms =
          retailer?.qualified_for_terms ??
          initialRetailer?.qualified_for_terms ??
          false;
        const isRetailerOnNetTerms =
          (isRetailerQualifiedForTerms &&
            (retailer?.maximum_available_payment_term_data?.is_net_terms ??
              initialRetailer?.maximum_available_payment_term_data
                ?.is_net_terms)) ??
          false;

        return {
          paymentOnShipmentTerm,
          paymentTermData:
            retailer?.payment_term_data ?? initialRetailer?.payment_term_data,
          maximumAvailablePaymentTerm:
            retailer?.maximum_available_payment_term ??
            initialRetailer?.maximum_available_payment_term ??
            PaymentTerm.UNSPECIFIED,
          maximumAvailablePaymentTermData:
            retailer?.maximum_available_payment_term_data ??
            initialRetailer?.maximum_available_payment_term_data,
          paymentTerm,
          isRetailerOnNetTerms,
          isRetailerQualifiedForTerms,
          isRetailerPreQualifiedForLimitIncrease:
            retailer?.net_terms?.pre_qualified_for_limit_increase ??
            initialRetailer?.net_terms?.pre_qualified_for_limit_increase ??
            false,
        };
      }),
    [
      retailer?.payment_on_shipment_term_data?.payment_term,
      retailer?.payment_term,
      retailer?.qualified_for_terms,
      retailer?.maximum_available_payment_term_data,
      retailer?.payment_term_data,
      retailer?.maximum_available_payment_term,
      retailer?.net_terms?.pre_qualified_for_limit_increase,
      initialRetailer?.payment_on_shipment_term_data?.payment_term,
      initialRetailer?.payment_term,
      initialRetailer?.qualified_for_terms,
      initialRetailer?.maximum_available_payment_term_data,
      initialRetailer?.payment_term_data,
      initialRetailer?.maximum_available_payment_term,
      initialRetailer?.net_terms?.pre_qualified_for_limit_increase,
    ]
  );

  return (
    <RetailerPaymentTermContext.Provider value={store}>
      {children}
    </RetailerPaymentTermContext.Provider>
  );
};

export const usePaymentTerm = () => {
  const context = useContext(RetailerPaymentTermContext);
  if (!context) {
    throw new Error(
      "RetailerPaymentTermContext not found. Must be used within RetailerPaymentTermProvider"
    );
  }
  return useStore(context);
};
